import { useState } from 'react';
import { ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import type { ModelProvider, ModelProviderSecret, PersistableModelProvider } from '@reflex/shared';
import {
  MODEL_PROVIDER_DOCS_URLS,
  MODEL_PROVIDER_LABELS,
  MODEL_PROVIDER_ORDER,
  PersistableModelProviderSchema,
} from '@reflex/shared';
import { formatDateOnly } from '@reflex/ui/lib/format';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { Button } from '@reflex/ui/components/ui/button';
import {
  AddModelProviderSecretDialog,
  type ScopeOptions,
  type ScopeSelection,
} from './AddModelProviderSecretDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@reflex/ui/components/ui/alert-dialog';

interface ModelProviderSecretsManagerProps {
  /** Heading rendered above the provider list. */
  title: string;
  /** Optional helper text under the heading. */
  description?: string;
  /** Loaded mps rows. Caller is responsible for fetching. */
  secrets: ModelProviderSecret[];
  isLoading?: boolean;
  isPending?: boolean;
  /** Optional read-only flag: hides Add/Edit/Delete affordances. */
  readOnly?: boolean;
  /**
   * Persist a new named key for the chosen provider. May return a
   * promise; when it rejects the add dialog stays open and surfaces the
   * error inline so the user can fix the key and resubmit.
   *
   * `scopeChoice` is only set when `scopeOptions` is provided; the host
   * page uses it to decide which (org/team/personal) API endpoint to
   * write to.
   */
  onCreate: (
    provider: PersistableModelProvider,
    name: string,
    value: string,
    scopeChoice?: ScopeSelection,
    baseUrl?: string | null,
  ) => void | Promise<unknown>;
  /**
   * Show an inline scope dropdown (Organization / Team / Personal) in the
   * add dialog. Used by the org-setup wizard so a first-time admin can
   * pick where the key lands. Omit on scope-fixed pages.
   */
  scopeOptions?: ScopeOptions;
  /**
   * Update an existing wrapper row: rename it, replace its value, or both.
   * `value` is omitted when only the name changed (keep the current
   * value). May return a promise; a rejection keeps the update dialog open
   * with an inline error.
   */
  onUpdate: (
    secretId: string,
    patch: { name?: string; value?: string; baseUrl?: string | null },
  ) => void | Promise<unknown>;
  /** Delete an existing wrapper row (and the underlying `sec_*`). */
  onDelete: (secretId: string) => void;
  /**
   * Optional override for the delete-confirm dialog body. Scope-fixed pages
   * that can enumerate dependents (the org tier) pass this to warn which
   * agents/personas/projects rely on the key; omitted, the dialog shows the
   * generic fallback text.
   */
  renderDeleteDescription?: (secret: {
    id: string;
    name: string;
    provider: ModelProvider;
  }) => React.ReactNode;
  /** Empty-state copy displayed when no providers are configured. */
  emptyState?: React.ReactNode;
  /** Optional override list of providers to render. Defaults to all five. */
  providers?: readonly PersistableModelProvider[];
  /**
   * When false, hide the section-header + button. Per-provider row Add
   * remains. Use on pages that put the page-level create CTA in
   * `PageHeader` instead.
   */
  showSectionAction?: boolean;
  /**
   * Optional controlled open state for the add dialog so a page-level
   * header button can open the same dialog the section/row actions use.
   */
  addOpen?: boolean;
  onAddOpenChange?: (open: boolean) => void;
}

/**
 * Provider-aware CRUD surface for `model_provider_secrets` (mps_*)
 * rows. Renders one group per supported provider; each group lists
 * every named API key the caller has in this scope (a provider can
 * hold multiple named keys), with Update/Delete per row and a
 * per-provider "Add key" affordance.
 *
 * Adding submits `{ provider, name, value, type: 'apiKey' }` to the
 * host page's mutation. Every key is explicitly named — there is no
 * implicit `default`. The presentational pattern intentionally mirrors
 * {@link SectionCard} so the personal/team/org pages can compose either
 * component without branching on tier.
 */
export function ModelProviderSecretsManager({
  title,
  description,
  secrets,
  isLoading = false,
  isPending = false,
  readOnly = false,
  onCreate,
  onUpdate,
  onDelete,
  emptyState,
  providers = MODEL_PROVIDER_ORDER,
  scopeOptions,
  renderDeleteDescription,
  showSectionAction = true,
  addOpen: addOpenControlled,
  onAddOpenChange,
}: ModelProviderSecretsManagerProps) {
  const [internalAddOpen, setInternalAddOpen] = useState(false);
  const [addProvider, setAddProvider] = useState<PersistableModelProvider | undefined>();
  const [addError, setAddError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ModelProviderSecret | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ModelProviderSecret | null>(null);

  const addControlled = onAddOpenChange != null;
  const addOpen = addControlled ? Boolean(addOpenControlled) : internalAddOpen;

  function openAdd(provider?: PersistableModelProvider) {
    setAddError(null);
    setAddProvider(provider);
    if (addControlled) onAddOpenChange(true);
    else setInternalAddOpen(true);
  }

  function setAddOpen(open: boolean) {
    if (!open) {
      setAddProvider(undefined);
      setAddError(null);
    }
    if (addControlled) onAddOpenChange(open);
    else setInternalAddOpen(open);
  }

  function openEdit(secret: ModelProviderSecret) {
    setEditError(null);
    setEditing(secret);
  }

  function errorText(err: unknown, fallback: string) {
    return err instanceof Error && err.message ? err.message : fallback;
  }

  const byProvider = new Map<ModelProvider, ModelProviderSecret[]>();
  for (const secret of secrets) {
    if (secret.type !== 'apiKey') continue;
    const list = byProvider.get(secret.provider);
    if (list) list.push(secret);
    else byProvider.set(secret.provider, [secret]);
  }
  for (const list of byProvider.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const existingNamesByProvider: Partial<Record<ModelProvider, readonly string[]>> = {};
  for (const [provider, list] of byProvider) {
    existingNamesByProvider[provider] = list.map((s) => s.name);
  }

  const configuredCount = byProvider.size;
  const showProviderGrid = configuredCount > 0 || !readOnly;

  const emptyContent =
    typeof emptyState === 'string' || emptyState == null ? (
      <EmptyState
        title={typeof emptyState === 'string' ? emptyState : 'No provider keys configured.'}
      />
    ) : (
      emptyState
    );

  return (
    <>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading model providers…</p>
      ) : (
        <SectionCard
          title={title}
          description={description}
          action={
            !readOnly && showSectionAction ? (
              // The org-model-providers tour spotlights this button, so the
              // hook is part of the contract — keep it if this action moves.
              // It is the only add-key control on the tier pages the
              // Credentials hub embeds: their page-level header buttons are
              // gated on `!embedded`. The hook also lands on the org-setup
              // wizard's copy of this manager, which no tour visits.
              <Button
                size="sm"
                onClick={() => openAdd()}
                disabled={isPending}
                data-tutorial-id="model-provider-add-key"
              >
                <Plus className="size-3.5" />
                Add key
              </Button>
            ) : undefined
          }
          contentClassName={showProviderGrid ? 'gap-0 divide-y py-0' : 'py-0'}
          className="gap-2 py-4 sm:gap-3"
          data-testid="model-provider-secrets-manager"
        >
          {showProviderGrid
            ? providers.map((provider) => {
                const keys = byProvider.get(provider) ?? [];
                return (
                  <div
                    key={provider}
                    className="space-y-2 py-2"
                    data-testid={`model-provider-group-${provider}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          {MODEL_PROVIDER_LABELS[provider]}
                        </span>
                        {MODEL_PROVIDER_DOCS_URLS[provider] ? (
                          <a
                            href={MODEL_PROVIDER_DOCS_URLS[provider]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-primary hover:underline"
                            aria-label={`Get ${MODEL_PROVIDER_LABELS[provider]} API key`}
                          >
                            Get key <ExternalLink className="size-3" />
                          </a>
                        ) : null}
                      </div>
                      {!readOnly && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAdd(provider)}
                          disabled={isPending}
                          aria-label={`Add ${MODEL_PROVIDER_LABELS[provider]} key`}
                        >
                          <Plus className="size-3" /> Add
                        </Button>
                      )}
                    </div>
                    {keys.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No keys configured.</p>
                    ) : (
                      <ul className="space-y-1">
                        {keys.map((secret) => (
                          <li
                            key={secret.id}
                            className="flex items-center justify-between gap-3 rounded-md bg-muted/40 px-3 py-2"
                            data-testid={`model-provider-key-${secret.id}`}
                          >
                            <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
                              <span className="text-sm font-medium">{secret.name}</span>
                              <span className="text-xs text-muted-foreground">
                                updated {formatDateOnly(secret.updatedAt)}
                              </span>
                            </div>
                            {!readOnly && (
                              <div className="flex shrink-0 items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEdit(secret)}
                                  disabled={isPending}
                                  aria-label={`Update ${MODEL_PROVIDER_LABELS[provider]} key ${secret.name}`}
                                >
                                  <Pencil className="size-3" /> Update
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setDeleting(secret)}
                                  disabled={isPending}
                                  aria-label={`Delete ${MODEL_PROVIDER_LABELS[provider]} key ${secret.name}`}
                                >
                                  <Trash2 className="size-3" />
                                </Button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })
            : emptyContent}
        </SectionCard>
      )}

      <AddModelProviderSecretDialog
        open={addOpen}
        onOpenChange={(open) => {
          if (!open) setAddError(null);
          setAddOpen(open);
        }}
        presetProvider={addProvider}
        existingNamesByProvider={existingNamesByProvider}
        providers={providers}
        scopeOptions={scopeOptions}
        onSubmit={async (provider, name, value, scopeChoice, baseUrl) => {
          setAddError(null);
          // The dialog's select only offers `providers` (persistable-only),
          // so this parse is a type narrowing, not a reachable failure.
          const persistable = PersistableModelProviderSchema.safeParse(provider);
          if (!persistable.success) return;
          try {
            await onCreate(persistable.data, name, value, scopeChoice, baseUrl);
            setAddOpen(false);
          } catch (err) {
            setAddError(errorText(err, 'Failed to save key. Please try again.'));
          }
        }}
        isPending={isPending}
        errorMessage={addError}
      />

      {editing && (
        <AddModelProviderSecretDialog
          open={true}
          onOpenChange={(next) => {
            if (!next) {
              setEditing(null);
              setEditError(null);
            }
          }}
          editSecret={{
            provider: editing.provider,
            name: editing.name,
            baseUrl: editing.baseUrl,
          }}
          existingNamesByProvider={existingNamesByProvider}
          providers={providers}
          errorMessage={editError}
          onSubmit={async (_provider, name, value, _scopeChoice, baseUrl) => {
            setEditError(null);
            const patch: { name?: string; value?: string; baseUrl?: string | null } = {};
            if (name !== editing.name) patch.name = name;
            if (value) patch.value = value;
            // baseUrl is `undefined` when the provider has no base-URL support;
            // only patch it when it actually changed from the stored value.
            if (baseUrl !== undefined && baseUrl !== (editing.baseUrl ?? null)) {
              patch.baseUrl = baseUrl;
            }
            // Nothing actually changed — just close.
            if (
              patch.name === undefined &&
              patch.value === undefined &&
              patch.baseUrl === undefined
            ) {
              setEditing(null);
              return;
            }
            try {
              await onUpdate(editing.id, patch);
              setEditing(null);
            } catch (err) {
              setEditError(errorText(err, 'Failed to update key. Please try again.'));
            }
          }}
          isPending={isPending}
        />
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(next) => {
          if (!next) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete{' '}
              {deleting
                ? `${MODEL_PROVIDER_LABELS[deleting.provider]} key "${deleting.name}"`
                : 'this key'}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription
              className={deleting && renderDeleteDescription ? 'sr-only' : undefined}
            >
              The wrapper row and its underlying encrypted secret will be removed. Agents that
              relied on this key will fall back to another key you select at launch.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* The dependency-aware body renders entity chips (`<button>`s), so
              it lives outside the description `<p>` to keep valid DOM. */}
          {deleting && renderDeleteDescription ? (
            <div className="text-sm">{renderDeleteDescription(deleting)}</div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) onDelete(deleting.id);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
